import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { assignAgentsToCompanyOrAgencyApi, deleteAccount, getAllRealtorsApi, getAllUsersApi, getUserProfile, unassignAgentFromCompanyOrAgencyApi, updateUser, getRealtorsByCompanyOrAgencyIdApi, getAllAgenciesOrRealtorsOrCompaniesApi } from "../api/user/userApi";
import { getUserStatus } from "../api/auth/authApi";
import { useUploadImagesStore } from "../store/useUploadImagesStore";
import { useAuthStore } from "../store/useAuthStore";
import { AllAgenciesOrRealtorsOrCompaniesType, UserResponse } from "../types/user/user.model";
import { toast } from "sonner";
import { t } from "i18next";

/**
 * A user's profile. My own comes from /auth/status: the public
 * /auth/profile/:id leaves out e-mail (and a private person's phone) for
 * everyone, me included (B13) — my profile form would come up half empty.
 */
export const useGetUserProfile = (userId: number) => {
  const myId = useAuthStore((s) => (s.isAuthenticated ? s.id : null));
  const own = Boolean(userId) && userId === myId;
  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => (own ? getUserStatus() : getUserProfile(userId)),
  });
};

export const useDeleteAccount = () => {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      logout();
      toast.success(t("profile.deleteAccount.success", "Аккаунт удалён"));
      navigate("/", { replace: true });
    },
    onError: () => {
      toast.error(
        t(
          "profile.deleteAccount.error",
          "Не удалось удалить аккаунт. Попробуйте снова.",
        ),
      );
    },
  });
};

export const useUpdateUser = () => {
  const setImages = useUploadImagesStore((s) => s.setImages);
  const setUserData = useAuthStore((s) => s.setUserData);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess: (_data, variables) => {
      setImages([]);
      // Keep the header/bottom-nav/comments avatar (all read from the global
      // auth store) and the profile page's own cached data in sync with what
      // was just saved — previously only the upload-preview state was reset,
      // so a fresh avatar/name never appeared anywhere until a full reload.
      setUserData(variables.userRequest);
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] });
    },
  });
};

export const useGetAllRealtors = () => {
  return useQuery({
    queryKey: ["agents"],
    queryFn: getAllRealtorsApi,
    select: (data) => {
      return data.filter((agent) => agent?.name)
    }
  });
};

export const useAssignAgentsToCompanyOrAgency = () => {
  return useMutation({
    mutationFn: assignAgentsToCompanyOrAgencyApi,
    onSuccess: () => {
      toast.success(t("realtor.assignSuccess"));
    },
    onError: () => {
      toast.error(t("realtor.assignError"));
    },
  });
};

export const useUnassignAgentsFromCompanyOrAgency = () => {
  return useMutation({
    mutationFn: unassignAgentFromCompanyOrAgencyApi,
    onSuccess: () => {
      toast.success(t("realtor.unassignSuccess", { defaultValue: "Риелтор успешно удален!" }));
    },
    onError: () => {
      toast.error(t("realtor.unassignError", { defaultValue: "Не удалось удалить риелтора" }));
    },
  });
}

export const useGetAllRealtorsByCompanyOrAgencyId = (companyOrAgencyId: number, page: number, offset: number) => {
  return useQuery({
    queryKey: ["agentsByCompanyOrAgencyId", companyOrAgencyId, page, offset],
    queryFn: () => getRealtorsByCompanyOrAgencyIdApi(companyOrAgencyId, page, offset),
  });
}

export const useGetAllAgenciesOrCompaniesOrRealtorsAccordingToType = (
  params: AllAgenciesOrRealtorsOrCompaniesType,
) => {
  return useQuery({
    queryKey: ['allAgenciesOrRealtorsOrCompanies', params],
    queryFn: () => getAllAgenciesOrRealtorsOrCompaniesApi(params),
  });
};